import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { adminApi } from '../services/api';
import { useNavigate } from 'react-router-dom';

interface AuthContextType {
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();

  const validateAndRefreshToken = useCallback(async () => {
    const token = localStorage.getItem('token');
    const refreshToken = localStorage.getItem('refresh_token');

    if (!token || !refreshToken) {
      setIsAuthenticated(false);
      return;
    }

    try {
      // Safely parse JWT token
      try {
        const tokenParts = token.split('.');
        if (tokenParts.length !== 3) {
          throw new Error('Invalid token format');
        }

        // Only try to refresh if the token is about to expire (within 5 minutes)
        const tokenData = JSON.parse(atob(tokenParts[1]));
        const expirationTime = tokenData.exp * 1000; // Convert to milliseconds
        const currentTime = Date.now();

        if (expirationTime - currentTime > 5 * 60 * 1000) {
          // 5 minutes
          setIsAuthenticated(true);
          return;
        }
      } catch (parseError) {
        // Invalid token format, clear and logout
        console.warn('Invalid token format, clearing auth:', parseError);
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        setIsAuthenticated(false);
        // Don't navigate if we're not on an admin page
        if (window.location.pathname.startsWith('/admin')) {
          navigate('/admin/login');
        }
        return;
      }

      // Try to refresh the token
      const response = await adminApi.refreshToken(refreshToken);
      if (response.data?.success) {
        const { token: newToken, refresh_token: newRefreshToken } = response.data.data;
        localStorage.setItem('token', newToken);
        localStorage.setItem('refresh_token', newRefreshToken);
        setIsAuthenticated(true);
      } else {
        throw new Error('Token refresh failed');
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      setIsAuthenticated(false);
      // Only navigate if we're on an admin page
      if (window.location.pathname.startsWith('/admin')) {
        navigate('/admin/login');
      }
    }
  }, [navigate]);

  useEffect(() => {
    validateAndRefreshToken();
    // Set up periodic token refresh (every 4 hours)
    const refreshInterval = setInterval(validateAndRefreshToken, 4 * 60 * 60 * 1000);
    return () => clearInterval(refreshInterval);
  }, [validateAndRefreshToken]);

  const login = useCallback(async (email: string, password: string) => {
    try {
      const response = await adminApi.login({ email, password });
      if (response.data?.success) {
        const { token, refresh_token } = response.data.data;
        localStorage.setItem('token', token);
        localStorage.setItem('refresh_token', refresh_token);
        setIsAuthenticated(true);
      } else {
        throw new Error('Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    setIsAuthenticated(false);
    navigate('/admin/login');
  }, [navigate]);

  const value = useMemo(
    () => ({ isAuthenticated, login, logout }),
    [isAuthenticated, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
